import { inject, Injectable } from '@angular/core';
import { _Service } from './_service';
import { AppId, EmailTemplate, EmailTemplateAttachment } from '../../model';
import { GenericSettingService } from './generic-setting.service';

@Injectable({ providedIn: 'root' })
export class EmailTemplateService extends _Service<EmailTemplate> {
  private genericSettingService = inject(GenericSettingService);

  constructor() {
    super('api/email-template');
  }

  getAttachments = async (templateId: AppId) =>
    await this.http.get<EmailTemplateAttachment[]>(`api/email-template/${templateId}/attachments`);

  getEmailWrapper = async () => (await this.genericSettingService.byName('email-wrapper')).value;

  findPlaceholders = (template: EmailTemplate) =>
    Object.fromEntries(
      (template.text.match(/\{\{([^}]+)}}/g) || [])
        .map((key) => key.slice(2, -2).trim())
        .map((key) => [key, this.getExampleValueForKey(key)])
    );

  generatePreview(
    template: EmailTemplate,
    context: Record<string, any>,
    emailWrapper: string,
    resources: EmailTemplateAttachment[]
  ): EmailTemplate {
    const content = Object.entries(context).reduce(
      (text, [key, value]) => text.replace(new RegExp('{{' + key + '}}', 'g'), value?.toString() || `«missing ${key}»`),
      template.text
    );

    const source = emailWrapper.replace('{{content}}', content);
    const text = resources.reduce(
      (text, { content, contentType, name }) =>
        text.replace(new RegExp(`cid:${name}`, 'g'), `data:${contentType};base64,${content}`),
      source
    );
    return { ...template, text };
  }

  private getExampleValueForKey(key: string): string {
    switch (key) {
      case 'baseName':
        return 'https://' + window.location.hostname;

      case 'name':
        return this.randomFrom(['John', 'Jane', 'Michael', 'Sarah', 'David']);

      case 'resetToken':
        return this.randomFrom([
          '5PXJ-H2BV-TEN8-6R0L',
          'A7BC-D9EF-G3HI-J5KL',
          'M2NP-Q8RS-T4UV-W6XY',
          'Z1AB-C3DE-F5GH-I7JK',
        ]);

      case 'invitedUserName':
        return this.randomFrom(['Alex', 'Taylor', 'Morgan', 'Jordan', 'Casey']);

      case 'userName':
        return this.randomFrom(['Robert', 'Emma', 'Thomas', 'Olivia', 'William']);

      case 'teamName':
        return this.randomFrom(['Supply Team', 'Logistics Department', 'Procurement Group', 'Operations Team']);

      case 'invitedUserEmail':
        return this.randomFrom(['user@example.com', 'contact@company.org', 'member@supplier.net', 'team@business.com']);

      case 'supplierName':
        return this.randomFrom(['Acme Supplies', 'Global Materials Inc.', 'EcoSource Ltd.', 'PrimeParts Co.']);

      case 'buyerName':
        return this.randomFrom(['MegaCorp Industries', 'TechGiant LLC', 'Global Enterprises', 'Innovative Solutions']);

      case 'sellerName':
        return this.randomFrom(['Timber Supply Co.', 'Forest Materials Ltd.', 'WoodSource International', 'Green Lumber Inc.']);

      case 'productName':
        return this.randomFrom(['Oak Lumber Bundle', 'Sustainable Hardwood', 'Certified Pine Planks', 'Reclaimed Timber']);

      case 'cancelledByName':
        return this.randomFrom(['John Smith', 'Maria Garcia', 'Thomas Mueller', 'Anna Wilson']);

      case 'inviteCode':
        return this.randomFrom(['INV-123456789', 'INV-987654321', 'INV-ABCDEF123', 'INV-XYZ789456']);

      case 'message':
        return this.randomFrom([
          "We're excited to collaborate with you on our supply chain documentation. Your input is valuable to us!",
          "I've added you to our SupplyCanvas platform to streamline our collaboration. Looking forward to working with you!",
          'Please join our SupplyCanvas workspace to help us meet regulatory requirements. Your participation is essential for our compliance efforts.',
          'We need your expertise to complete our supply chain documentation. Your prompt response would be greatly appreciated!',
          'Join us on SupplyCanvas to enhance our supply chain transparency. Your contribution is key to our success!',
        ]);

      case 'companyName':
        return this.randomFrom([
          'SupplyTech Solutions',
          'Global Logistics Inc.',
          'EcoMaterials Ltd.',
          'PrecisionParts Manufacturing',
          'Sustainable Supply Co.',
        ]);

      case 'ownerName':
        return this.randomFrom([
          'Jennifer Smith',
          'Marcus Johnson',
          'Sophia Chen',
          'Daniel Rodriguez',
          'Olivia Williams',
        ]);

      case 'trialLeft':
        return this.randomFrom(['28', '21', '14', '7', '3']);

      case 'trialStart':
        return this.randomFrom(['2025-03-01', '2025-03-10', '2025-03-15', '2025-03-20', '2025-03-25']);

      default:
        return `??${key}??`;
    }
  }

  private randomFrom = <T>(items: T[]): T => items[Math.floor(Math.random() * items.length)];
}
